/**
 * Purpose:
 *   Manage the collapsed desktop menu popover visibility and viewport-safe positioning.
 *
 * Notes:
 *   - Keeps the nested routes accessible when the sidebar is collapsed
 *   - Renders via portal-friendly coordinates so overflow containers do not clip the panel
 *   - Uses a short close delay so pointer travel from trigger to popover feels stable
 */

'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 90;
const POPOVER_PANEL_GAP = 16;
const VIEWPORT_PADDING = 8;
const ARROW_SAFE_PADDING = 22;
const POPOVER_TRANSLATE_Y_OFFSET = 24;

export interface ICollapsedMenuPopoverPosition {
  left: number;
  top: number;
  arrowOffsetTop: number;
}

export interface IUseCollapsedMenuPopoverReturn {
  popoverRef: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  canPortal: boolean;
  position: ICollapsedMenuPopoverPosition | null;
  setAnchorElement: (element: HTMLElement | null) => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleFocus: () => void;
  handleBlur: (event: React.FocusEvent<HTMLElement>) => void;
}

export function useCollapsedMenuPopover(): IUseCollapsedMenuPopoverReturn {
  const anchorRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] =
    useState<ICollapsedMenuPopoverPosition | null>(null);
  const canPortal = typeof document !== 'undefined';

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current === null) return;
    window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }, []);

  const openPopover = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const setAnchorElement = useCallback((element: HTMLElement | null) => {
    anchorRef.current = element;
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;

    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const renderedOffset = popoverRect.height / 2 - POPOVER_TRANSLATE_Y_OFFSET;

    const preferredLeft = anchorRect.right + POPOVER_PANEL_GAP;
    const maxLeft = window.innerWidth - popoverRect.width - VIEWPORT_PADDING;
    const left = Math.min(preferredLeft, Math.max(maxLeft, VIEWPORT_PADDING));

    const centeredTop =
      anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
    const minTop = VIEWPORT_PADDING - renderedOffset;
    const maxTop =
      window.innerHeight - popoverRect.height - renderedOffset - VIEWPORT_PADDING;
    const top = Math.min(
      Math.max(centeredTop, minTop),
      Math.max(maxTop, VIEWPORT_PADDING)
    );
    const anchorCenterY = anchorRect.top + anchorRect.height / 2;
    const renderedTop = top + renderedOffset;
    const arrowOffsetTop = Math.min(
      Math.max(anchorCenterY - renderedTop, ARROW_SAFE_PADDING),
      Math.max(popoverRect.height - ARROW_SAFE_PADDING, ARROW_SAFE_PADDING)
    );

    setPosition({ left, top, arrowOffsetTop });
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const nextTarget = event.relatedTarget;

    if (
      nextTarget instanceof Node &&
      (anchorRef.current?.contains(nextTarget) ||
        popoverRef.current?.contains(nextTarget))
    ) {
      return;
    }

    scheduleClose();
  }, [scheduleClose]);

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  useLayoutEffect(() => {
    if (!canPortal || !isOpen) return;

    updatePosition();

    const handleViewportChange = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [canPortal, isOpen, updatePosition]);

  return {
    popoverRef,
    isOpen,
    canPortal,
    position,
    setAnchorElement,
    handleMouseEnter: openPopover,
    handleMouseLeave: scheduleClose,
    handleFocus: openPopover,
    handleBlur,
  };
}
